import React, { useEffect, useState } from "react";
import {
	useLocation,
} from 'react-router-dom';
import cx from 'classnames';
import { gql } from "@apollo/client";
import {
	object as yup_object,
	string as yup_string,
} from "yup";
import {
	parse as qs_parse,
	stringify as qs_stringify,
} from 'qs';
import TakeOverLayout from "layouts/TakeOverLayout.js";
import CmsContentList from "components/data/CmsContentList.js";
import PreventDefault from "utils/PreventDefault.js";
import CmsContentRenderer from "components/data/CmsContentRenderer.js";
import Button from "components/Button.js";
import CustomerRouteKeys  from "CustomerRouteKeys.js";
import { getPathByRoute } from "App.js";
import useFormHelper from "utils/form-helper/useFormHelper";
import { useLogoutContext } from "components/auth/Logout.js";
import useStdQuery from "components/data/hooks/useStdQuery.js";
import { GET_EMAIL } from "components/data/session-user/SessionUser.js";
import LoadingIcon from "components/icons/LoadingIcon.js";
import { graphqlErrorMiddleware } from "utils/error-handling/graphql/GraphqlClientMiddleware.js";
import useStandardMutation from "components/data/hooks/useStandardMutation.js";
import { WireFormHelper } from "utils/FormHelper.js";
import Container from 'components/Container.js';

import { useGlobalToastsContext } from "context/ToastProvider.js";
import Toast, { TYPE_ERROR } from "components/Toast.js";
import { socialAuth } from 'pages/auth/Register.js';
import Password from 'components/forms/Password.js';
import { SSO_PROVIDERS } from "utils/constants/Sso.js";
import {
	Facebook,
	SocialMediaIcon,
} from 'components/Icon.js';

import * as loginStyles from "pages/auth/Login.module.css";
import { BO_ERRORS, COLLECTIONS, getErrorKey } from "utils/GetErrorKey";
import { Redirect } from "react-router-dom/cjs/react-router-dom.min";

const cms = {
	header: 'miscText.confirm-account-email-update-header',
	socialSubheader: 'miscText.confirm-account-email-update-subheader',
	googleBtnTitle: 'miscText.confirm-account-email-update-google-button-label',
	facebookBtnTitle: 'miscText.confirm-account-email-update-facebook-button-label',
	or_Conjunction: "miscText.register-or",
	confirmWithPasswordText: 'miscText.confirm-account-email-update-password-text',
	passwordInputLabel: 'miscText.confirm-account-email-update-password-label',
	forgotPasswordCTA: 'miscText.signin-email-forgotPasswordCTA',
	submit: 'miscText.confirm-account-email-update-submit-button-label',
	toastTitle: 'miscText.confirm-account-email-update-toast-title',
	toastText: 'miscText.confirm-account-email-update-toast-text',
};

const UPDATE_EMAIL = gql`
	mutation updateEmail(
		$email: String!
		$newEmail: String!
		$password: String!
	) {
		CustomerMutationRoute {
			updateEmail(
				email: $email
				newEmail: $newEmail
				password: $password
			)
		}
	}
`;

const getYupSchema = () => {
	const validations = {
		password: yup_string()
			.required(getErrorKey(COLLECTIONS.registration, 'contact.password', BO_ERRORS.general.required)),
	};
	return yup_object().shape(validations);
};


const ConfirmAccountUpdateEmail = () => {
	const [ updateEmailMutation ] = useStandardMutation(UPDATE_EMAIL);
	const [ redirect, setRedirect ] = useState(null);

	const location = useLocation();
	const { newEmail } = location.state ?? {};

	const { data: emailData, loading: getEmailLoading } = useStdQuery(GET_EMAIL);

	const logoutAndClearCache = useLogoutContext();

	const {
		setToast,
		removeToast,
	} = useGlobalToastsContext();

	const {
		formRef,
		formHelper,
		submitting, setSubmitting,
	} = useFormHelper({ getYupSchema });

	useEffect(() => {
		// We've somehow lost the new email in state so we need to not let the user proceed with the flow
		if (!newEmail) {
			setRedirect(<Redirect push to={{
				pathname: getPathByRoute(CustomerRouteKeys.AccountPersonalInformation),
			}} />);
		}},[ newEmail ]);

	if (getEmailLoading) {
		return <LoadingIcon />;
	}
	const currentEmail = emailData?.session?.customer?.contact.email;

	const handleConfirmAccountFailed = (cmsContent) => {
		setRedirect(<Redirect push to={{
			pathname: getPathByRoute(CustomerRouteKeys.AccountPersonalInformation),
		}} />);

		setToast(<Toast
			type={TYPE_ERROR}
			title={<CmsContentRenderer
				cmsContent={cmsContent}
				contentKey={cms.toastTitle}
				fallbackValue='Unable to confirm your account'
			/>}
			text={<CmsContentRenderer
				cmsContent={cmsContent}
				contentKey={cms.toastText}
				fallbackValue='We were unable to confirm your account, so your email address change was not saved. Please check your account login information and try again.'
			/>}
			onClosed={removeToast}
		/>);
	};

	const kickoffSubmit = async (cmsContent) => {
		setSubmitting(true);

		let validated;

		try {
			validated = await formHelper.startValidation();
			formHelper.clearAllErrors();
		} catch (errorReport) {
			setSubmitting(false);
			formHelper.validationErrorHandler(errorReport);
			return;
		}

		try {
			await graphqlErrorMiddleware(updateEmailMutation({ variables: { email: currentEmail, newEmail, password: validated.password } }));
		} catch (errorReport) {
			formHelper.validationErrorHandler(errorReport);
			setSubmitting(false);

			if (errorReport.TopLevelKey === 'miscText.confirm-account-email-update-error') {
				handleConfirmAccountFailed(cmsContent);
			}
		}
		setSubmitting(false);

		logoutAndClearCache({
			redirectTo: getPathByRoute(CustomerRouteKeys.VerifyUpdateEmail) + "?" + qs_stringify({ isUsernameEncrypted: false, username: newEmail }),
		});
	};

	if (redirect) {
		return redirect;
	}

	return (
		<CmsContentList list={Object.values(cms)}>{({ cmsContent }) => (
			<TakeOverLayout
				title={<CmsContentRenderer contentKey={cms.header} fallbackValue="Confirm your Charlie account" />}
				data-qa="confirmAccountUpdateEmailTakeover"
				showCancel
			>
				<div className={loginStyles.container}>
					<WireFormHelper {...{ formHelper }}>
						<form
							className={loginStyles.verifyEmailContainer}
							data-qa="confirmAccountForm"
							ref={formRef}
							onSubmit={PreventDefault(() => kickoffSubmit(cmsContent))}
						>
							<Container overrideClass={loginStyles.container}>
								<section className={cx(loginStyles.content, loginStyles.registerContent)}>
									<div className={loginStyles.socialBtnContainer}>
										<CmsContentRenderer.P
											className={loginStyles.subheadAlt}
											data-qa="confirmSocialTitle"
											contentKey={cms.socialSubheader}
											fallbackValue='Confirm with your social account:'
										/>
										<div className={loginStyles.actions}>
											<button className={cx(loginStyles.socialBtn, loginStyles.socialBtnGP)}
												data-qa="GoogleConfirmBtn"
												onClick={PreventDefault(() => socialAuth(SSO_PROVIDERS.GOOGLE.key, setRedirect))}
												aria-label={cmsContent[ cms.googleBtnTitle ]}
											>
												<SocialMediaIcon socialMediaType={SSO_PROVIDERS.GOOGLE.key} childProps={{ overrideClass: cx(loginStyles.socialBtnIcon, loginStyles.socialBtnIconGP) }} />
												<CmsContentRenderer.Span
													contentKey={cms.googleBtnTitle}
													fallbackValue='Confirm with Google'
												/>
											</button>
											<button className={cx(loginStyles.socialBtn, loginStyles.socialBtnFB)}
												data-qa="FBConfirmBtn"
												onClick={PreventDefault(() => socialAuth(SSO_PROVIDERS.FACEBOOK.key, setRedirect))}
												aria-label={cmsContent[ cms.facebookBtnTitle ]}
											>
												<Facebook overrideClass={cx(loginStyles.socialBtnIcon, loginStyles.socialBtnIconFB)}
													aria-hidden={true}
													data-qa="FBLogo"
												/>
												<CmsContentRenderer.Span
													contentKey={cms.facebookBtnTitle}
													fallbackValue='Confirm with Facebook'
												/>
											</button>
										</div>

									</div>

									<div className={loginStyles.divider}>
										<CmsContentRenderer.P
											className={loginStyles.dividerText}
											contentKey={cms.or_Conjunction}
											fallbackValue='OR'
										/>
									</div>

									<div className={loginStyles.formContainer}>
										<CmsContentRenderer.P
											className={loginStyles.subheadAlt}
											data-qa="ConfirmWithPasswordHeader"
											contentKey={cms.confirmWithPasswordText}
											fallbackValue='Confirm with your password:'
										/>

										<div>
											<div className={loginStyles.registerPasswords}>
												<Password
													label={cmsContent[ cms.passwordInputLabel ] || 'Password'}
													name="password"
													autoComplete="new-password"
													data-qa="ConfirmInputPassword"
													error={formHelper.getFieldError('password')}
													aria-labelledby="passwordRequirements"
												/>

											</div>
											{formHelper.getFieldErrorJsx('')}
											<div className={loginStyles.verifyFormActions}>
												<Button
													additionalClassNames={loginStyles.verifyButton}
													isPrimary={true}
													data-qa="SubmitConfirmEmailUpdateBtn"
													submitting={submitting}
												>
													<CmsContentRenderer contentKey={cms.submit} fallbackValue="Submit" />
												</Button>
											</div>
										</div>
									</div>
								</section>
							</Container>
						</form>
					</WireFormHelper>
				</div>
			</TakeOverLayout>
		)}</CmsContentList>
	);
};

export default ConfirmAccountUpdateEmail;
